<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden"
  >
    <div
      class="max-w-xl w-full bg-white/80 backdrop-blur-xl p-8 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10 py-16"
    >
      <ShineBorder 
        :border-width="2" 
        :duration="24" 
        :shine-color="['#A07CFE', '#FE8FB5', '#FFBE7B']" 
      />
      <div class="relative flex items-center justify-center">
        <div class="absolute -top-[25rem] z-10 bg-[#4d7aff] w-[30rem] h-[30rem] rounded-full blur-[100px] opacity-30 appear"></div>
        <LogoMainLink class="z-20" />
      </div>

      <h1 class="mt-8 text-center text-2xl text-gray-900 text-pretty font-light">
        Reset your password
      </h1>
      <p class="text-center text-sm text-gray-600 mt-4">
        Enter your email and we'll send you a link to get back into your account.
      </p>

      <Form
        @submit="submitForgotPassword"
        class="mt-8 space-y-6 max-w-[20rem] mx-auto"
      >
        <div class="space-y-4">
          <FormField v-slot="{ componentField }" name="email">
            <FormItem>
              <FormControl>
                <Input
                  v-bind="componentField"
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  v-model="email"
                  required
                />
              </FormControl>
            </FormItem>
          </FormField>
        </div>

        <!-- Error message if any -->
        <div v-if="errorMessage" class="text-red-500 text-sm mt-2">{{ errorMessage }}</div>

        <!-- Success message -->
        <div v-if="successMessage" class="text-green-500 text-sm mt-2">{{ successMessage }}</div>

        <div>
          <Button
            type="submit"
            :disabled="isLoading || !email || successMessage !== null"
            class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200"
            :style="{ opacity: isLoading || successMessage !== null ? 0.5 : 1 }"
          >
            <span v-if="!isLoading">Reset password</span>
            <span v-else>Sending email...</span>
          </Button>
        </div>

        <div class="flex items-center justify-center mt-6">
          <div class="text-center text-sm text-gray-600">
            Remember your password?
            <router-link to="/login" class="font-medium text-indigo-600 hover:text-indigo-500">
              Sign in
            </router-link>
          </div>
        </div>
      </Form>
    </div>
    <!-- Terms and Conditions -->
    <TermsAndConditionsLinks />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Form, FormField, FormItem, FormControl } from '@/components/ui/form'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import TermsAndConditionsLinks from '@/components/common/TermsAndConditionsLinks.vue'
import LogoMainLink from '@/components/common/LogoMainLink.vue'

const email = ref<string>('')
const isLoading = ref(false)
const errorMessage = ref<string | null>(null)
const successMessage = ref<string | null>(null)

const submitForgotPassword = async () => {
  isLoading.value = true
  errorMessage.value = null
  successMessage.value = null

  try {
    await api.post(API_ROUTES.USER_FORGOT_PASSWORD, {
      email: email.value,
      link: window.location.origin
    })
    successMessage.value = 'A reset password link has been sent to your email.'
  } catch (error: any) {
    if (error.response?.data?.error) {
      errorMessage.value = error.response.data.error
    } else if (error.response?.data?.detail) {
      errorMessage.value = error.response.data.detail
    } else {
      errorMessage.value = 'Failed to send reset link. Please try again later.'
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.appear {
  animation: appear 1.5s ease-in-out;
}

@keyframes appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
}
</style>
